import { type Key, type RefCallback, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

type Options = {
  hasMore: boolean;
  isLoading: boolean;
  itemCount: number;
  onLoadMore?: () => void;
  root: Element | null;
  threshold: number;
  resetKey?: Key;
};

export function useInfiniteScroll({ hasMore, isLoading, itemCount, onLoadMore, root, threshold, resetKey }: Options) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [requestedItemCount, setRequestedItemCount] = useState<number | undefined>(undefined);
  const requestedItemCountRef = useRef(requestedItemCount);
  const onLoadMoreRef = useLatestRef(onLoadMore);
  const [prevSnapshot, setPrevSnapshot] = useState({ itemCount, hasMore, isLoading });
  const [prevResetKey, setPrevResetKey] = useState(resetKey);

  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setRequestedItemCount(undefined);
  }

  if (
    prevSnapshot.itemCount !== itemCount ||
    prevSnapshot.hasMore !== hasMore ||
    prevSnapshot.isLoading !== isLoading
  ) {
    const shouldClearRequest =
      itemCount < prevSnapshot.itemCount ||
      (hasMore && !prevSnapshot.hasMore) ||
      (prevSnapshot.isLoading && !isLoading);
    setPrevSnapshot({ itemCount, hasMore, isLoading });
    if (shouldClearRequest) {
      setRequestedItemCount(undefined);
    }
  }

  const targetRef: RefCallback<HTMLElement> = useCallback((element) => {
    setTarget(element);
  }, []);

  useLayoutEffect(() => {
    requestedItemCountRef.current = requestedItemCount;
  }, [requestedItemCount]);

  useEffect(() => {
    if (!hasMore || isLoading || !onLoadMoreRef.current) {
      return;
    }

    const requestMore = () => {
      if (requestedItemCountRef.current === itemCount) {
        return;
      }

      requestedItemCountRef.current = itemCount;
      setRequestedItemCount(itemCount);
      onLoadMoreRef.current?.();
    };

    if (!target) {
      if (itemCount === 0) {
        requestMore();
      }
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          requestMore();
        }
      },
      { root, rootMargin: `0px 0px ${threshold}px 0px` },
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, itemCount, root, target, threshold, onLoadMoreRef]);

  return targetRef;
}
